'use client';

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { WatchlistItem, useStore } from '@/lib/store';
import { BuddyService, BuddyLink } from '@/lib/buddies';
import { toast } from 'sonner';
import { Users, Link2, Eye, EyeOff, X, Clock } from 'lucide-react';

// Per-show progress sharing ("Watch Together"). Renders inside the item
// detail dialog for TV shows on the user's watchlist. Only this show's
// progress is shared with linked friends; the hide toggle opts the show out
// of all sharing.

export function WatchTogetherSection({ item }: { item: WatchlistItem }) {
  const { user } = useAuth();
  const { updateWatchlistItem } = useStore();
  const [buddies, setBuddies] = useState<BuddyLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const hidden = !!item.hiddenFromSharing;

  const loadBuddies = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const links = await BuddyService.listBuddies(item.id);
      setBuddies(links);
      // Repair stale self-progress (e.g. show added/watched after accepting)
      BuddyService.reconcileMyProgress(links, user.uid, [item], updateWatchlistItem);
    } catch (error) {
      // Silent — section just shows the invite button
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, item.id]);

  useEffect(() => {
    loadBuddies();
  }, [loadBuddies]);

  const handleInvite = async () => {
    setCreating(true);
    try {
      const { link } = await BuddyService.createInvite(item.id);
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied — send it to your friend!');
      await loadBuddies();
    } catch (e: any) {
      toast.error(e.message || 'Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyPending = (buddy: BuddyLink) => {
    navigator.clipboard.writeText(`${window.location.origin}/buddy/${buddy.id}`);
    toast.success('Invite link copied');
  };

  const handleLeave = async (buddy: BuddyLink) => {
    try {
      await BuddyService.leave(buddy.id);
      await loadBuddies();
      toast.success('Progress unlinked');
    } catch (error) {
      toast.error('Failed to unlink');
    }
  };

  const handleToggleHidden = async () => {
    try {
      await updateWatchlistItem(item.id, { hiddenFromSharing: !hidden });
      toast.success(hidden ? 'Show can be shared again' : 'Show hidden from all sharing');
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (!user) return null;

  return (
    <div className="px-4 py-4 border-b border-white/10">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base md:text-lg font-semibold tracking-tight text-white flex items-center gap-2">
          <Users className="h-5 w-5 text-red-500" />
          Watch Together
        </h3>
        <button
          onClick={handleToggleHidden}
          className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full transition-colors ${
            hidden
              ? 'bg-red-950/50 text-red-400 ring-1 ring-red-600/40'
              : 'bg-white/5 text-gray-400 ring-1 ring-white/10 hover:text-white'
          }`}
          title={hidden ? 'This show is hidden from all sharing' : 'Hide this show from all sharing'}
        >
          {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {hidden ? 'Hidden' : 'Visible'}
        </button>
      </div>

      {hidden ? (
        <p className="text-sm text-gray-500">
          This show is hidden from sharing. Friends can&apos;t be invited and it won&apos;t appear in any shared view.
        </p>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">
            Link with a friend to see each other&apos;s progress on this show — and only this show.
          </p>

          {/* Linked friends */}
          {!loading && buddies.length > 0 && (
            <div className="space-y-2 mb-3">
              {buddies.map((buddy) => {
                const otherUid = buddy.participants.find(p => p !== user.uid);
                const otherName = otherUid ? buddy.names?.[otherUid] : null;
                const otherProgress = otherUid ? buddy.progress?.[otherUid] : null;
                const myProgress = buddy.progress?.[user.uid];

                return (
                  <div key={buddy.id} className="rounded-xl bg-white/5 ring-1 ring-white/10 p-3 flex items-center gap-3">
                    {buddy.status === 'pending' ? (
                      <>
                        <Clock className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white">Open invite — any friend can accept</p>
                          <button onClick={() => handleCopyPending(buddy)} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                            <Link2 className="h-3 w-3" /> Copy invite link
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{otherName || 'Friend'}</p>
                        <p className="text-xs text-gray-400">
                          {otherProgress?.lastEpisode
                            ? <>Last watched <span className="text-white font-medium">{otherProgress.lastEpisode}</span> · {otherProgress.episodesWatched} episodes</>
                            : 'No episodes watched yet'}
                        </p>
                        {myProgress && otherProgress && (
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            {myProgress.episodesWatched === otherProgress.episodesWatched
                              ? "You're even!"
                              : myProgress.episodesWatched > otherProgress.episodesWatched
                                ? `You're ${myProgress.episodesWatched - otherProgress.episodesWatched} episode${myProgress.episodesWatched - otherProgress.episodesWatched !== 1 ? 's' : ''} ahead`
                                : `You're ${otherProgress.episodesWatched - myProgress.episodesWatched} episode${otherProgress.episodesWatched - myProgress.episodesWatched !== 1 ? 's' : ''} behind`}
                          </p>
                        )}
                      </div>
                    )}
                    <button
                      onClick={() => handleLeave(buddy)}
                      className="p-1.5 rounded-full text-gray-500 hover:text-red-400 hover:bg-red-950/40 transition-colors flex-shrink-0"
                      title={buddy.status === 'pending' ? 'Cancel invite' : 'Unlink progress'}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={handleInvite}
            disabled={creating}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-white/10 ring-1 ring-white/20 text-white hover:bg-white/20 backdrop-blur-md px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            {creating ? 'Creating link...' : 'Invite a friend'}
          </button>
        </>
      )}
    </div>
  );
}
